import type {
  UserData,
  ApiResponse,
  BackupListResponse,
  PasswordVerifyResult,
  RecoveryResult,
  PasswordMode,
  IntakeItem,
} from '../types'

const API_BASE = '/api'

/**
 * Error thrown when the server responds with a non-2xx status. Distinct from
 * network errors (TypeError from fetch) so callers can tell "server said no"
 * from "couldn't reach server".
 */
export class ApiHttpError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiHttpError'
  }
}

/** True when the thrown error is a fetch network failure (offline, DNS, CORS). */
export function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    const msg = (body as { error?: string }).error || `API error: ${response.status}`
    throw new ApiHttpError(response.status, msg)
  }
  return response.json()
}

// GET user data from KV
export async function fetchUserData(
  userId: string,
): Promise<ApiResponse<{ key: string; value: UserData } | null>> {
  const response = await fetch(`${API_BASE}/storage/${encodeURIComponent(userId)}`)
  return parseResponse(response)
}

// PUT user data to KV
export async function saveUserData(userId: string, data: UserData): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/storage/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value: JSON.stringify(data) }),
  })
  return parseResponse(response)
}

// List available backups
export async function listBackups(userId: string): Promise<BackupListResponse> {
  const response = await fetch(`${API_BASE}/storage/${encodeURIComponent(userId)}?backups=list`)
  return parseResponse(response)
}

// Get a specific backup
export async function getBackup(
  userId: string,
  timestamp: string,
): Promise<ApiResponse<{ key: string; value: UserData; isBackup: boolean }>> {
  const response = await fetch(
    `${API_BASE}/storage/${encodeURIComponent(userId)}?backups=${encodeURIComponent(timestamp)}`,
  )
  return parseResponse(response)
}

// Set or change password
export async function setPassword(
  userId: string,
  password: string,
  currentPassword?: string,
  passwordMode: PasswordMode = 'on_load',
  timeoutMinutes?: number,
): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/user/${encodeURIComponent(userId)}/password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password, currentPassword, passwordMode, timeoutMinutes }),
  })
  return parseResponse(response)
}

// Verify password
export async function verifyPassword(
  userId: string,
  password: string,
): Promise<PasswordVerifyResult> {
  const response = await fetch(`${API_BASE}/user/${encodeURIComponent(userId)}/password/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  })
  return parseResponse(response)
}

// Public contact form → emails the site owner via Resend
export async function sendContact(name: string, email: string, message: string): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, message }),
  })
  return parseResponse(response)
}

// Request a password-reset email (always succeeds; no enumeration)
export async function requestPasswordReset(userId: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/user/${encodeURIComponent(userId)}/password/reset/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  })
  return parseResponse(response)
}

// Consume a reset token and set a new password
export async function resetPassword(userId: string, token: string, password: string): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/user/${encodeURIComponent(userId)}/password/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  })
  return parseResponse(response)
}

// Remove password
export async function removePassword(
  userId: string,
  currentPassword: string,
): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/user/${encodeURIComponent(userId)}/password`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword }),
  })
  return parseResponse(response)
}

// Update settings (backup email, password mode, timeout)
export async function updateSettings(
  userId: string,
  settings: {
    backupEmail?: string
    passwordMode?: PasswordMode
    timeoutMinutes?: number
    currentPassword?: string
  },
): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/user/${encodeURIComponent(userId)}/settings`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  })
  return parseResponse(response)
}

// DELETE user data from KV
export async function deleteUserData(userId: string): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/storage/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  })
  return parseResponse(response)
}

// Send recovery email
export async function sendRecoveryEmail(email: string): Promise<RecoveryResult> {
  const response = await fetch(`${API_BASE}/recover`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  return parseResponse(response)
}

export type FoodLookupResult =
  | { ok: true; name: string; items: IntakeItem[] }
  | { ok: false; reason: 'unconfigured' | 'failed' | 'unidentified' | 'ratelimited'; message: string }

/**
 * Decompose free-text food/drink into itemised nutrition via the server-side
 * Claude lookup. Never throws — failure modes are returned so the caller can
 * fall back to manual entry.
 */
export async function lookupFood(query: string): Promise<FoodLookupResult> {
  let res: Response
  try {
    res = await fetch(`${API_BASE}/lookup/food`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    })
  } catch {
    return { ok: false, reason: 'failed', message: 'Network error' }
  }
  if (res.status === 503) return { ok: false, reason: 'unconfigured', message: 'Nutrition lookup is not configured.' }
  if (res.status === 429) return { ok: false, reason: 'ratelimited', message: 'Too many lookups — try again shortly.' }
  if (!res.ok) return { ok: false, reason: 'failed', message: `Lookup error (${res.status}).` }

  const body = (await res.json().catch(() => ({}))) as {
    success?: boolean
    data?: { name: string; items: IntakeItem[] }
    error?: string
  }
  if (!body.success || !body.data) {
    return { ok: false, reason: 'unidentified', message: body.error || 'Could not identify that item.' }
  }
  return { ok: true, name: body.data.name, items: body.data.items }
}

/** Check whether a user has password protection (and its mode). */
export async function checkPasswordProtection(userId: string): Promise<{
  hasPassword: boolean
  passwordMode?: PasswordMode
  timeoutMinutes?: number
}> {
  const result = await fetchUserData(userId)
  if (result.success && result.data?.value) {
    const u = result.data.value
    return {
      hasPassword: !!(u.passwordHash && u.passwordSalt),
      passwordMode: u.passwordMode,
      timeoutMinutes: u.timeoutMinutes,
    }
  }
  return { hasPassword: false }
}
